import { Middleware } from 'redux';
import RootState from 'store/state';
import AppTypes from 'store/app/app.types';
import MessagesTypes from 'store/messages/messages.types';
import listenToIframeMessages from './iframeListener';
import listenToLoaderMessages, { invalidateChatLoaderWebsocket, setChatLoaderWebsocket } from './loaderListener';
import AnyAction from 'store/anyAction';
import { debouncer, checkNetworkConnection, postMessageToParentPromise } from 'utils';
import { setMode } from 'store/app/app.actions';
import getChatSession from 'services/chatSession/getChatSession';
import createChatSession from 'services/chatSession/createChatSession';
import { ChatMessageEvent, IChatMessage } from 'store/messages/messages.state';
import { postMessageToParent } from 'utils'
import { v4 as uuid } from 'uuid'
import { messageMap } from 'services/messageMap';
import { bchatRetry, SmartagentChatWebsocketType } from '@missionlabs/smartagent-bchat';
import { MessageUserType } from '@missionlabs/smartagent-chat-components-lib';
import { mapTypeName } from 'views/TextArea';
import listenToChatSession from './chatSessionListener';
import exportTranscript from './exportTranscript';
import * as Logger from '../../../services/logger'
import { IChatSessionParams } from 'services/chatSession/chatSession';
import { connectBChat } from './chatSessionListener/chatSessionListenerUtils';

const chatMiddleware: Middleware<{}, RootState> = store => next => {

    let session: connect.CustomerChatSession | undefined | null;
    let ws: SmartagentChatWebsocketType;

    const displayMap = messageMap();

    const sendTypingEvent = debouncer({
        onCall: () => {
            if (!session || !ws) return;

            const contentType = "application/vnd.amazonaws.connect.event.typing";

            bchatRetry(async () => session?.sendEvent({
                contentType
            }))

            window.bchat.putChatEvent({
                contactId: session.getChatDetails().contactId,
                event: contentType as ChatMessageEvent
            })
        },
        debounceTimeMs: 2000
    });

    const convertMessage = (msg: string) => {
        const emails = msg.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi);
        msg = msg.replace(/./g, '*')

        emails?.forEach(x => {
            msg = msg.replace(x.replace('_', '*'), x.replace('_', '\\_'));
        })
        return msg;
    }

    return async (action: AnyAction) => {
        switch (action.type) {
            case AppTypes.INIT_APP: {
                ws?.close();
                session = null
                performance.mark('bchat-connect-start');
                const { apiKey,websocketUrl } = action.payload.connectionDetails
                ws = await connectBChat({
                    apiKey, websocketUrl
                })
                performance.mark('bchat-connect-end')

                window.bchat = ws;

                const sessionParams: IChatSessionParams = {
                    ws: ws,
                    connectionDetails: action.payload.connectionDetails,
                    store,
                    restoreSession: action.payload.restoreSession,
                    globalRestoreSession: action.payload.globalRestoreSession,
                    attachmentsEnabled: action.payload.attachmentsConfig?.enabled
                }
                
                connect.ChatSession.setGlobalConfig({
                    // @ts-ignore
                    features:{
                        messageReceipts: {
                            shouldSendMessageReceipts: true,
                            thorttleTime: 5000
                        }
                    },
                    // @ts-ignore
                    throttleTime: 5000 // This prop isn't listed in documentation but seems to affect the throttleTime
                })

                performance.mark('loadChatSession-start')
                if (action.payload.restoreSession) {
                    session = await getChatSession(sessionParams)
                }

                if (!session) {
                    session = await createChatSession(sessionParams)
                }
                performance.mark('loadChatSession-end')

                if (!session) {
                    const hasNetworkConnection = checkNetworkConnection()
                    // if the getChatSession fails and the browser has no internet connection display a more generic error
                    const errorMessage = hasNetworkConnection ? "Due to extremely high traffic, live chat is unavailable at this time." : 'Due to a network error, we are unable to load live chat at this time.'
                    console.log(`Unable to get chat session. The network is ${hasNetworkConnection ? 'Online' : "Offline"} `)

                    return store.dispatch({
                        type: AppTypes.INIT_APP_ERROR,
                        payload: {
                            errorMessage: errorMessage
                        }
                    });
                };

                ws.addMessageListener((data: any) => {
                    if (data.eventType === "ChatEvent") {
                        store.dispatch({
                            type: AppTypes.CHAT_EVENT,
                        })
                    }
                })

                performance.mark('listen-to-chat-session-start')

                await listenToChatSession(session, store, ws, action.payload.restoreSession);

                performance.mark('listen-to-chat-session-end')

                listenToIframeMessages(store);

                performance.mark('init-app-end')


                window.chatPerformance = () => {
                    performance.clearMeasures();
                    performance.measure('init-app', 'init-app-start', 'init-app-end')
                    performance.measure('get-chat-config', 'get-config-start', 'get-config-end')
                    performance.measure('connect-to-bchat', 'bchat-connect-start', 'bchat-connect-end')
                    performance.measure('loadChatSession-start', 'loadChatSession-start', 'loadChatSession-end')
                    performance.measure('listen-to-chat-session', 'listen-to-chat-session-start', 'listen-to-chat-session-end')

                    const initAppMeasure = performance.getEntriesByName('init-app')
                    const table: any = []

                    performance.getEntriesByType("measure")
                    .sort((a, b) => {
                        return a.startTime - b.startTime
                    })
                    .forEach(m => {
                        table.push({
                            metric: m.name,
                            duration: m.duration.toFixed(1),
                            runtime: `${(m.startTime - initAppMeasure[0].startTime).toFixed(1)} - ${(m.startTime + m.duration - initAppMeasure[0].startTime).toFixed(1)}`
                        })
                    })
                    console.table(table)
                }

                return next({
                    ...action,
                    payload: {
                        ...action.payload,
                        contactId: session.getChatDetails().contactId
                    }
                });
            }

            case AppTypes.INIT_DISCONNECT_FLOW: {
                const appState = store.getState().app
                const previousContactID = session?.getChatDetails().contactId

                const {
                    connectionDetails,
                    instanceID,
                    restoreSession,
                    globalRestoreSession,
                    attachmentsConfig,
                } = appState
                
                postMessageToParent('start-disconnect-flow', {})
                
                const previousAttributes = await ws.getContactAttributes({
                    contactId: previousContactID!,
                    instanceId: connectionDetails.instanceId || instanceID,
                });

                await ws.updateContact({
                    attributes: {
                        'sa-customer-disconnect-flow-initiated': 'true'
                    },
                    contactId: previousContactID!
                })

                const sessionParams: IChatSessionParams = {
                    ws: ws,
                    connectionDetails: action.payload.connectionDetails,
                    store,
                    restoreSession: action.payload.restoreSession,
                    globalRestoreSession: action.payload.globalRestoreSession,
                    attachmentsEnabled: action.payload.attachmentsConfig?.enabled,
                    startDisconnectChatFlow: true,
                    previousChatId: previousContactID,
                    previousAttributes
                }

                try {
                    console.log(
                        '[chatMiddleware] calling session.disconnectParticipant() for previous session',
                        {
                            contactId: previousContactID,
                        },
                    )
                    await session?.disconnectParticipant()
                } catch (err) {
                    Logger.error('disconnect failed', err)
                }

                session = await createChatSession(sessionParams)

                if (!session) {
                    Logger.error('[chatMiddleware] createChatSession failed for disconnect flow')
                    return store.dispatch({
                        type: AppTypes.INIT_APP_ERROR,
                        payload: {
                            errorMessage: "There was an error connecting you to the disconnect flow. Chat ended."
                        }
                    });
                }

                const contactId = session?.getChatDetails().contactId;

                // Inform listeners by attaching minimal payload when forwarding the action
                const forwardPayload: { contactId?: string; previousContactID?: string } = {}
                if (contactId) forwardPayload.contactId = contactId
                if (previousContactID) forwardPayload.previousContactID = previousContactID

                await listenToChatSession(session, store, ws, action.payload.restoreSession);
                setChatLoaderWebsocket(ws);
                listenToLoaderMessages(store);
                listenToIframeMessages(store);

                return next({
                    ...action,
                    payload: forwardPayload,
                })
            }

            case AppTypes.END_CHAT: {
                type PayloadAction = {
                    type: string
                    payload?: { preventClosingChatWindow?: boolean }
                }
                const act = action as PayloadAction

                invalidateChatLoaderWebsocket()
                const { ended, globalRestoreSession } = store.getState().app
                try {
                    if (!ended) {
                        if (globalRestoreSession) {
                            postMessageToParent('delete-smartagent-global-persistance', {})
                        }
                    }
                } catch (err) {
                    Logger.error('Unable to disconnect participant', err)
                    console.log('sa-log error in session.disconnectParticipant() ', err)
                }

                if (!act?.payload?.preventClosingChatWindow) {
                    await postMessageToParentPromise(
                        'smartagent-minimise-loader',
                        'smartagent-minimise-loader-finished',
                    )
                }

                return next({
                    ...action,
                })
            }
  
            case AppTypes.CUSTOMER_ENDED_CHAT: {
                type PayloadAction = { type: string; payload?: unknown }
                const act = action as PayloadAction
                console.log('[chatMiddleware] CUSTOMER_ENDED_CHAT received', {
                    actionPayload: act.payload,
                    appStateSnapshot: store.getState().app,
                })
                invalidateChatLoaderWebsocket()
                const { ended, customerDisconnectFlowStarted } = store.getState().app
                const { customerDisconnectFlowId } = store.getState().app.connectionDetails
                try {
                    console.log(
                        '[chatMiddleware] about to disconnect participant for CUSTOMER_ENDED_CHAT',
                        {
                            contactId: session?.getChatDetails().contactId,
                            ended,
                            customerDisconnectFlowStarted,
                            customerDisconnectFlowId,
                        },
                    )

                    if (!ended && (customerDisconnectFlowStarted || !customerDisconnectFlowId)) {
                        store.dispatch({ type: AppTypes.END_CHAT })
                    }

                    await session?.disconnectParticipant()
                } catch (err) {
                    Logger.error('Unable to disconnect participant when CUSTOMER_ENDED_CHAT', err)
                }

                return next({
                    ...action,
                })
            }
            case MessagesTypes.SEND_FILE:
                const sendFileResult = await session?.sendAttachment({
                    attachment: action.payload.file,
                })

                if (!sendFileResult?.data) throw new Error("Couldn't send file.")

                const content = {
                    en: {
                        type: 'INVISIBLE_MESSAGE',
                        participant: MessageUserType.CUSTOMER
                    }
                }

                await bchatRetry(async () => await session?.sendMessage({
                    message: JSON.stringify(content),
                    contentType: 'text/plain'
                }), (timesRetried, error) => {
                    Logger.warn(`Session.sendMessage retried. Times: ${timesRetried}`, error)
                })



                const objectUrl = window.URL.createObjectURL(action.payload.file);
                const fileUploadMessage: IChatMessage = {
                    id: uuid(),
                    type: 'ATTACHMENT',
                    content: {
                        type: 'FILE_LINK',
                        src: objectUrl,
                        label: action.payload.file.name || 'Document Uploaded',
                        //@ts-ignore
                        fileType: mapTypeName(action.payload.file.type),
                        isImage: action.payload.file.type.split('/')[0] === 'image',
                        onDownload: ((fileName: string) => () => {
                            const downloadLink = document.createElement('a')
                            downloadLink.setAttribute('download', fileName ?? 'Download')
                            downloadLink.setAttribute('href', objectUrl);
                            document.body.appendChild(downloadLink)
                            downloadLink.click()
                            downloadLink.remove()
                        })(action.payload.file.name)
                    },
                    originalMessage: "",
                    contentType: 'text/plain',
                    displayName: 'Customer',
                    role: 'CUSTOMER',
                    sentAt: new Date().toUTCString(),
                    sendStatus: 'unknown',
                    isJson: true,
                }
                         
                action.payload = fileUploadMessage;
                return next(action);
            case MessagesTypes.SEND_MESSAGE:

                if (typeof action.payload.message === 'string') {
                    console.log('connect sending message', action.payload, { ws });

                    const sendMessageResult = await bchatRetry(async () => await session?.sendMessage({
                        contentType: "text/plain",
                        message: action.payload.message as string
                    }), (timesRetried, error) => {
                        Logger.warn(`Session.sendMessage retried. Times: ${timesRetried}`, error)
                    });

                    if (!sendMessageResult?.data) throw new Error('couldnt get message data');

                    const toDisplay = action.payload.displayAs ?? action.payload.message

                    const content = store.getState().app.mode === "SECURE"
                        ? convertMessage(toDisplay)
                        : toDisplay;

                    const message: IChatMessage = {
                        id: sendMessageResult.data.Id,
                        content,
                        contentType: "text/plain",
                        displayName: "Customer",
                        role: "CUSTOMER",
                        originalMessage: content,
                        sentAt: sendMessageResult.data.AbsoluteTime,
                        sendStatus: "unknown",
                        isJson: true,
                        type: 'MESSAGE'
                    };

                    if (action.payload.displayAs) {
                        displayMap.set(sendMessageResult.data.Id, toDisplay)
                    }

                    action.payload.message = message;

                    console.log('debug', action.payload);

                    return next(action)
                }

                return next(action);
            case AppTypes.SET_MESSAGE_INPUT:
                if (action.payload && !store.getState().app.ended) {
                    sendTypingEvent();
                }
                return next(action);
            case MessagesTypes.NEW_MESSAGE:
                const lastMessage = [action.payload].flat().pop()!;

                const isAttachment = !!lastMessage.attachments
                const isAgentMessage = lastMessage?.role !== 'CUSTOMER';
                const isText = lastMessage?.contentType === 'text/plain';

                console.log({lastMessage});
                
                
                if (isAgentMessage && (isText || isAttachment)) {
                    postMessageToParent('new-message-notification', {
                        content: isAttachment ? "Attachment" : lastMessage.content,
                        displayName: lastMessage.displayName,
                        contentType: lastMessage.contentType,
                        sender: lastMessage.role,
                    });
                }

                if (!lastMessage?.content || typeof lastMessage.content === 'string') {
                    return next(action);
                }

                if (lastMessage.content.inputType !== store.getState().app.mode) {
                    store.dispatch(setMode(lastMessage.content.inputType))
                }

                return next(action);

            case MessagesTypes.MARK_CHAT_MESSAGE_RECEIPTS_AS_READ:
                const messages = store.getState().messages;
                const unreadAgentMessages = messages.filter(message => (
                    message.role === 'AGENT' &&
                    ["text/markdown", "text/plain"].includes(message.contentType) &&
                    !message.readTimestamp
                ));

                if (!unreadAgentMessages?.length) return;

                unreadAgentMessages?.forEach(unreadMessage => {
                    session?.sendEvent({
                        content: JSON.stringify({ messageId: unreadMessage.id }),
                        //@ts-ignore
                        contentType: ChatMessageEvent.MESSAGE_READ,
                    })
                });

                return next(action);

            case MessagesTypes.GET_TRANSCRIPT:
                exportTranscript(store);
                return next(action)

            default:
                return next(action);
        }
    }
};

export default chatMiddleware;
